'use strict';

import {getDeviceId} from '../../utilities/userStorageAPI/localStorageAPI';
import {report} from '../../utilities/tagManager/tagManager';
import ReactPixel from 'react-facebook-pixel';
import stageConfig from 'app/StageConfig';

// GA4 event wrapper
const gtagDebounceMap = new Map();

export const gtag4 = (params, delay = 1000) => {
  if (typeof window === 'undefined') return;

  const {event, ...sendParams} = params;
  const eventKey = `gtag4_${event}_${JSON.stringify(sendParams)}`;

  const pushEvent = () => {
    if (stageConfig.stage === 'dev') {
      console.log('[GTM - DEV] Mock dataLayer.push called with:', {
        event,
        ...sendParams,
      });
      return;
    }

    console.log(`[GTM] Event: ${event} | Send Params: ${JSON.stringify(sendParams, null, 2)}`);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      ...sendParams,
    });

    gtagDebounceMap.delete(eventKey);
  };

  // Cancel previous scheduled push if exists
  if (gtagDebounceMap.has(eventKey)) {
    clearTimeout(gtagDebounceMap.get(eventKey));
  }

  const timeoutId = setTimeout(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(pushEvent, {timeout: 2000});
    } else {
      pushEvent();
    }
  }, delay);

  gtagDebounceMap.set(eventKey, timeoutId);
};

// GA4 page view
const pageViewDebounceMap = new Map();

export const gaPageView = (page, delay = 1000) => {
  if (typeof window === 'undefined') return;

  const customDeviceId = getDeviceId();
  const pagePath = window.location.pathname + page;
  const eventKey = `page_view_${pagePath}`;

  const payload = {
    event: 'page_view',
    page_path: pagePath,
    user_properties: {
      device_id: customDeviceId,
    },
  };

  const pushEvent = () => {
    if (stageConfig.stage === 'dev') {
      console.log('[GTM - DEV] Mock page_view:', JSON.stringify(payload, null, 2));
      return;
    }

    console.log(`[GTM] Page View: ${pagePath} | Device ID: ${customDeviceId}`);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    pageViewDebounceMap.delete(eventKey);
  };

  // Cancel previous scheduled push if exists
  if (pageViewDebounceMap.has(eventKey)) {
    clearTimeout(pageViewDebounceMap.get(eventKey));
  }

  const timeoutId = setTimeout(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(pushEvent, {timeout: 2000});
    } else {
      pushEvent();
    }
  }, delay);

  pageViewDebounceMap.set(eventKey, timeoutId);
};

// GA4 custom event with debounce
// Uses a map to debounce events based on category, action, and label
const debounceMap = new Map();

export const gaEvent = (category, action, label, delay = 1000) => {
  if (typeof window === 'undefined') return;

  const eventKey = `${category}_${action}_${label}`;
  const customDeviceId = getDeviceId();

  const payload = {
    event: action,
    event_category: category,
    event_label: label,
    user_properties: {
      device_id: customDeviceId,
    },
  };

  const pushEvent = () => {
    if (stageConfig.stage === 'dev') {
      console.log('[GTM - DEV] Mock custom event:', JSON.stringify(payload, null, 2));
      return;
    }

    console.log(`[GTM] Custom Event: ${action} | Category: ${category} | Label: ${label} | Device ID: ${customDeviceId}`);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  // Cancel previous scheduled event if exists
  if (debounceMap.has(eventKey)) {
    clearTimeout(debounceMap.get(eventKey));
  }

  // Schedule new event with debounce
  const timeoutId = setTimeout(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(pushEvent, {timeout: 2000});
    } else {
      pushEvent();
    }
    debounceMap.delete(eventKey); // cleanup
  }, delay);

  debounceMap.set(eventKey, timeoutId);
};

// Facebook Pixel custom event
const fbqDebounceMap = new Map();

export const fbqEvent = (event, data, delay = 1000) => {
  const eventKey = `fbq_${event}_${JSON.stringify(data)}`;

  const pushEvent = () => {
    console.log(`[FB Pixel] Event: ${event} | Data: ${JSON.stringify(data, null, 2)}`);
    ReactPixel.trackCustom(event, data);
    fbqDebounceMap.delete(eventKey);
  };

  // Cancel previous scheduled push if exists
  if (fbqDebounceMap.has(eventKey)) {
    clearTimeout(fbqDebounceMap.get(eventKey));
  }

  const timeoutId = setTimeout(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(pushEvent, {timeout: 2000});
    } else {
      pushEvent();
    }
  }, delay);

  fbqDebounceMap.set(eventKey, timeoutId);
};

// Internal reporting
const reportDebounceMap = new Map();

export const gaReportFormSuccessAllWebSiteEvent = (sector, formName, delay = 1000) => {
  const eventKey = `FormSuccessAllWebsite_${sector}_${formName}`;

  const tagItem = {
    Sector: sector,
    FormName: formName,
    event: 'FormSuccessAllWebsite',
  };

  const pushReport = () => {
    console.log(`[Internal Report] Form Success | Sector: ${sector} | Form: ${formName}`);
    report(tagItem);
    reportDebounceMap.delete(eventKey);
  };

  if (reportDebounceMap.has(eventKey)) {
    clearTimeout(reportDebounceMap.get(eventKey));
  }

  const timeoutId = setTimeout(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(pushReport, {timeout: 2000});
    } else {
      pushReport();
    }
  }, delay);

  reportDebounceMap.set(eventKey, timeoutId);
};

// Utility function
export const switchExtraction = (paramsDes, paramsMobile) => {
  const dataWithoutTags = (paramsDes ?? paramsMobile)
    ?.replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return dataWithoutTags ?? '';
};
